import { ReactNode, useContext } from 'react';

import { Chip, Skeleton, Tooltip } from '@mui/material';
import { MuiPickersAdapterContext } from '@mui/x-date-pickers-pro';
import { formatDistanceToNow } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  mapLinkSourceToSuffixTranslationKey,
  mapPreviousLinkToTranslationKey,
} from '../utils';

import * as S from '../styles';

interface RenderHeaderProps {
  linkSource: string | undefined;
  previousType: string | undefined;
  companyName: string | undefined;
  userExpirationDate: string | undefined;
  linkCreatedAt: string | undefined;
  isLoading: boolean;
}

export const RenderHeader: React.FC<RenderHeaderProps> = ({
  linkSource,
  previousType,
  companyName,
  userExpirationDate,
  linkCreatedAt,
  isLoading,
}) => {
  const intl = useIntl();
  const localization = useContext(MuiPickersAdapterContext);

  const renderChip = () => {
    if (isLoading) {
      return <Skeleton variant="rectangular" width="80px" height="24px" />;
    }

    if (linkSource && previousType) {
      return (
        <Tooltip
          title={intl.formatMessage({
            id: mapPreviousLinkToTranslationKey(previousType),
          })}
          arrow
          placement="top"
        >
          <Chip
            size="small"
            variant="filled"
            label={intl.formatMessage({
              id: mapLinkSourceToSuffixTranslationKey(linkSource),
            })}
          />
        </Tooltip>
      );
    }
    return null;
  };

  const renderCompanyInfo = () => {
    if (isLoading) {
      return <Skeleton variant="rectangular" width="200px" height="24px" />;
    }

    return (
      <S.CompanyWrapper>
        <S.Company data-testid="companyName">
          <FormattedMessage
            id="CAREMATRIXCPTC.HEADER.COMPANY"
            values={{
              companyName,
              b: (chunks: ReactNode[]) => <span>{chunks}</span>,
            }}
          />
        </S.Company>
      </S.CompanyWrapper>
    );
  };

  const renderTimeChip = () => {
    if (isLoading) {
      return <Skeleton variant="rectangular" width="120px" height="24px" />;
    }
    if (userExpirationDate) {
      return (
        <Chip
          size="small"
          variant="filled"
          label={intl.formatMessage(
            { id: 'CAREMATRIXCPTC.COMPANY.ELIGIBILITYSHUTDOWN' },
            {
              date: formatDistanceToNow(userExpirationDate, {
                locale: localization?.utils?.locale,
              }),
            },
          )}
        />
      );
    }
    return (
      <Chip
        size="small"
        variant="filled"
        label={intl.formatMessage(
          { id: 'CAREMATRIXCPTC.LABEL.TIME' },
          {
            number: formatDistanceToNow(linkCreatedAt || '', {
              locale: localization?.utils?.locale,
            }),
          },
        )}
      />
    );
  };

  return (
    <>
      <S.Header>{renderChip()}</S.Header>
      <S.Header>
        {renderCompanyInfo()}
        {renderTimeChip()}
      </S.Header>
    </>
  );
};
